export type Deployment = 'on-premise' | 'private-cloud' | 'hybrid';

export type PartnerLogo = { src: string; name: string };

export type Project = {
  slug: string;
  name: string;
  tagline: string;
  description: string;
  client: string;
  clientCountry: string;
  industry: string;
  deployment: Deployment;
  techStack: string[];
  highlights: string[];
  logo?: string;          // path from /public
  logoInvertible?: boolean; // whether the logo should be inverted in dark mode
  image?: string;         // path from /public
  liveUrl?: string;       // optional public-facing site for the deployment
  partners?: PartnerLogo[]; // partner / customer logos to surface on the detail page
  featured: boolean;
  featuredDescription: string; // one-liner for homepage
};

export const PROJECTS: Project[] = [
  {
    slug: 'olingo-medical',
    name: 'Olingo Medical',
    tagline: 'Voice & documents → FHIR records',
    description:
      'Ollsoft integrated an AI module into the Medicalc Hospital Information System, automating administrative work across public hospitals. Voice recordings from examinations, surgeries, ward rounds, and ambulance visits are transcribed and converted into structured clinical documentation and FHIR records by the Olingo Medical LLM. Physical documents are digitised via Olingo OCR. The system is deployed as an air-gapped on-premise installation on dedicated GPU servers, ensuring full data sovereignty — patient data never leaves the hospital network.',
    client: 'Multiple public hospitals',
    clientCountry: 'Czech Republic',
    industry: 'Healthcare',
    deployment: 'on-premise',
    techStack: ['Olingo Speech', 'Olingo OCR', 'Olingo Medical LLM', 'REST / MEX API', 'Medicalc HIS', 'GPU on-premise servers'],
    highlights: [
      '10+ public hospital deployments',
      'Air-gapped — data never leaves the hospital',
      'Speaker separation for ambulance, surgery, autopsy, ward rounds',
      'FHIR-structured output from raw voice',
      'OCR for printed medical reports and external documents',
      'Direct integration with Medicalc via MEX exchange server',
    ],
    logo: '/partners/ollingo-logo.webp',
    logoInvertible: false,
    image: '/work/olingo-medical.png',
    liveUrl: 'https://olingomedical.com',
    partners: [
      { src: '/partners/fnhk.webp',             name: 'Fakultní nemocnice Hradec Králové' },
      { src: '/partners/fn-plzen.png',          name: 'Fakultní nemocnice Plzeň' },
      { src: '/partners/penta-hospitals.png',   name: 'Penta Hospitals' },
      { src: '/partners/nemocnice-nachod.png',  name: 'Nemocnice Náchod' },
      { src: '/partners/logo-nem-rychnov.webp', name: 'Nemocnice Rychnov nad Kněžnou' },
      { src: '/partners/euc.png',               name: 'EUC — European Healthcare Network' },
      { src: '/partners/medicalc.png',          name: 'Medicalc HIS' },
    ],
    featured: true,
    featuredDescription: 'Voice & documents → FHIR records. Live in 10+ public hospitals across the Czech Republic.',
  },
  {
    slug: 'derantwortpartner',
    name: 'DerAntwortpartner',
    tagline: 'CRM + document archive → instant AI answers',
    description:
      'Ollsoft built an AI-powered knowledge management system for Die Netzwerkpartner e.V., a German consulting association. The system connects to their Microsoft Dynamics CRM, SharePoint document library, and the German Energy Industry Act (EnWG), enabling consultants to generate sourced, accurate answers to member inquiries in real time. The application uses a RAG architecture with a vector database, hosted on EU infrastructure to ensure full GDPR compliance. A continuous feedback loop refines relevance over time.',
    client: 'Die Netzwerkpartner e.V.',
    clientCountry: 'Germany',
    industry: 'Consulting / Energy',
    deployment: 'private-cloud',
    techStack: ['Azure (Sweden Central)', 'Weaviate', 'GPT-4 / Mistral Large', 'MS Dynamics 365', 'SharePoint', 'FastAPI', 'Svelte', 'Docker'],
    highlights: [
      'RAG over CRM records, SharePoint, and legal text (EnWG)',
      'Direct MS Dynamics 365 integration',
      'EU-hosted — fully GDPR compliant',
      'Chat mode for iterative questioning',
      'Feedback loop for continuous relevance improvement',
    ],
    logo: '/partners/die-netzwerkpartner.webp',
    logoInvertible: false,
    image: '/work/derantwortpartner.png',
    partners: [
      { src: '/partners/die-netzwerkpartner.webp', name: 'Die Netzwerkpartner e.V.' },
    ],
    featured: false,
    featuredDescription: 'CRM + document archive → instant AI answers for 100+ consultants.',
  },
  {
    slug: 't-systems-connectivity',
    name: 'T-Systems Connectivity Platform',
    tagline: 'Fleet connectivity management with predictive analytics',
    description:
      'Ollsoft developed a Managed Connectivity Platform for T-Systems International that unifies fleet internet connectivity management into a single interface. The platform aggregates multiple network APIs, allows administrators to manage SIM lifecycles, monitor data usage, track software updates, and predict future bandwidth requirements using ML models trained on historical fleet data.',
    client: 'T-Systems International GmbH',
    clientCountry: 'Germany',
    industry: 'Automotive / Telecommunications',
    deployment: 'on-premise',
    techStack: ['Python / FastAPI', 'Docker', 'Dedicated server infrastructure', 'ML prediction models', 'T-IoT Hub API', 'YAML config-driven architecture'],
    highlights: [
      'Unified API aggregation across multiple network providers',
      'Interactive fleet map with connectivity status',
      'ML-driven data volume forecasting',
      'SIM lifecycle management at fleet scale',
      'Pre-flight checks before critical OTA software updates',
    ],
    logo: '/partners/t-systems.png',
    logoInvertible: false,
    image: '/work/t-systems.png',
    partners: [
      { src: '/partners/t-systems.png', name: 'T-Systems International GmbH' },
    ],
    featured: true,
    featuredDescription: 'Fleet connectivity platform with ML-driven predictive analytics — on T-Systems infrastructure.',
  },
  {
    slug: 'heggel-advisor',
    name: 'Heggel AI Advisor',
    tagline: 'AI technical consultant for industrial coatings',
    description:
      'Ollsoft built a 24/7 AI-powered technical consultant and lead generation tool for Heggel GmbH. The system guides users through a three-step process: describe a project scenario, answer a dynamic AI-generated questionnaire, then receive a product recommendation from the Heggel portfolio with full technical reasoning — grounded in historical project data and Technical Data Sheets via RAG. Delivered in three weeks to meet a trade show deadline.',
    client: 'Heggel GmbH',
    clientCountry: 'Germany',
    industry: 'Industrial / Manufacturing',
    deployment: 'private-cloud',
    techStack: ['React', 'FastAPI', 'Weaviate', 'Azure LLMs', 'RAG architecture'],
    highlights: [
      'Dynamic AI questionnaire per scenario',
      'RAG over Technical Data Sheets and project history',
      'Full product reasoning for customers and technical staff',
      'Delivered in 3 weeks for trade show launch',
      'Transitioned to managed service with transparent Azure pass-through',
    ],
    logo: '/partners/heggel.png',
    logoInvertible: false,
    image: '/work/heggel.png',
    liveUrl: 'https://ai.heggelgmbh.com',
    partners: [
      { src: '/partners/heggel.png', name: 'Heggel GmbH' },
    ],
    featured: false,
    featuredDescription: 'AI-powered 24/7 technical consultant — deployed in 3 weeks for Heggel GmbH.',
  },
  {
    slug: 'anywhere-legal',
    name: 'Anywhere Legal AI Workspace',
    tagline: 'AI-assisted legal document management',
    description:
      'Ollsoft enhanced the Anywhere Legal platform with AI to manage legal case management and international cooperation. The solution features a central AI workspace unifying scoping, pricing, and research tools. The core component is an AI-assisted document editor enabling lawyers to draft and modify contracts with generative AI assistance. Olingo Speech models handle legal transcription. All processing runs on private EU cloud infrastructure to ensure data sovereignty and client confidentiality.',
    client: 'Anywhere.Legal',
    clientCountry: 'Czech Republic',
    industry: 'Legal',
    deployment: 'private-cloud',
    techStack: ['Olingo Speech', 'Generative AI document builder', 'Private EU cloud', 'Automated partner selection'],
    highlights: [
      'AI document editor for drafting and modifying contracts',
      'Central AI orchestration across scoping, pricing, research',
      'Legal transcription via Olingo Speech models',
      'Automated partner selection based on case requirements',
      'Private EU cloud — full client confidentiality',
    ],
    logo: '/partners/anywhere-legal.png',
    logoInvertible: false,
    image: '/work/anywhere-legal.png',
    liveUrl: 'https://anywhere.legal',
    partners: [
      { src: '/partners/anywhere-legal.png', name: 'Anywhere.Legal' },
    ],
    featured: false,
    featuredDescription: 'AI workspace for legal document creation and international case management.',
  },
  {
    slug: 'eurowag-legislation',
    name: 'Eurowag Legislation Monitor',
    tagline: 'Multi-jurisdiction legal intelligence platform',
    description:
      'Ollsoft built an automated legislation monitoring platform for Eurowag, tracking regulatory changes across EU, Czech, and Slovak jurisdictions that affect the fuel and telematics sectors. LLMs analyse official gazettes, legal newsletters, and lobbying activity, filtering based on Eurowag\'s specific business profile. A daily traffic-light report (red/orange/green) helps legal teams focus on critical compliance actions. Precision deep-linking scrolls directly to the relevant paragraph in source documents.',
    client: 'Eurowag',
    clientCountry: 'Czech Republic / EU',
    industry: 'Fuel & Telematics',
    deployment: 'on-premise',
    techStack: ['Mistral Large (fine-tuned)', 'Dagster orchestration', 'Web scraping (EU/CZ/SK registers)', 'Traffic-light reporting dashboard'],
    highlights: [
      'Monitors EU, CZ, and SK legislative registers',
      'Daily traffic-light email briefings (red/orange/green)',
      'Precision deep-links to exact paragraphs in source PDFs',
      'AI feedback loop reduces false positives over time',
      'Zero-IT deployment — no internal IT integration required',
    ],
    logo: '/partners/eurowag.png',
    logoInvertible: true,
    image: '/work/eurowag.png',
    partners: [
      { src: '/partners/eurowag.png', name: 'Eurowag' },
    ],
    featured: true,
    featuredDescription: 'Multi-jurisdiction legislation monitor — daily traffic-light compliance reports for Eurowag.',
  },
  {
    slug: 'rowanai',
    name: 'RowanAI',
    tagline: 'Background AI agents running law firm operations — airgapped, on-premise',
    description:
      'Ollsoft built an airgapped, on-premise AI agent infrastructure for Rowan Legal. Multiple autonomous agents run continuously in the background, handling the operational layer of the firm: matter intake classification, internal knowledge retrieval, research pipeline orchestration, document routing, and compliance checks. Nothing is sent to an external API — all inference runs locally on dedicated GPU hardware inside the firm\'s network. Client matter data, case files, and internal correspondence stay entirely within the building.',
    client: 'Rowan Legal',
    clientCountry: 'Czech Republic',
    industry: 'Legal',
    deployment: 'on-premise',
    techStack: ['Multi-agent orchestration', 'Local LLM inference', 'Dedicated on-premise GPU servers', 'Vector search (local)', 'Custom fine-tuned legal models'],
    highlights: [
      'Fully airgapped — no external API calls, all inference on-premise',
      'Autonomous agents handle matter intake, routing, and classification',
      'Internal knowledge retrieval across case files and firm precedents',
      'Research pipeline agents surface relevant case law automatically',
      'Compliance checks run as background processes, not manual reviews',
      'Client matter data never leaves the firm\'s network',
    ],
    logo: '/partners/rowan-legal.png',
    logoInvertible: false,
    image: '/work/rowanai.png',
    partners: [
      { src: '/partners/rowan-legal.png', name: 'Rowan Legal' },
    ],
    featured: true,
    featuredDescription: 'Airgapped AI agent infrastructure running law firm operations at Rowan Legal — no data leaves the building.',
  },
  {
    slug: 'byteship',
    name: 'ByteShip',
    tagline: 'AI-powered log analysis IDE',
    description:
      'ByteShip is an AI-powered IDE designed for log file analysis and debugging. The system uses AI models to parse, interpret, and surface actionable insights from complex log outputs, dramatically reducing the time engineers spend on manual debugging.',
    client: 'Internal / B2B SaaS',
    clientCountry: '',
    industry: 'Developer Tooling',
    deployment: 'on-premise',
    techStack: ['AI log parsing', 'IDE interface', 'Custom inference models', 'On-premise inference server'],
    highlights: [
      'AI-powered log parsing and pattern recognition',
      'Dramatically reduces manual debugging time',
      'Structured insights from raw log output',
    ],
    logo: undefined,
    logoInvertible: false,
    image: '/work/byteship.png',
    featured: false,
    featuredDescription: 'AI-powered IDE for log file analysis and intelligent debugging.',
  },
];

// German translations of the case-study content. The English fields above are
// the source and stay the /en (and fallback) locale; these only apply on /de.
// Deployment values, tech stack, and proper-noun clients are intentionally
// left untranslated. /cs falls back to English until a Czech set exists.
type ProjectDE = Partial<Pick<Project, 'tagline' | 'description' | 'client' | 'clientCountry' | 'industry' | 'highlights'>>;

const PROJECT_DE: Record<string, ProjectDE> = {
  'olingo-medical': {
    tagline: 'Sprache & Dokumente → FHIR-Datensätze',
    client: 'Mehrere öffentliche Krankenhäuser',
    clientCountry: 'Tschechien',
    industry: 'Gesundheitswesen',
    description:
      'Ollsoft hat ein KI-Modul in das Krankenhausinformationssystem Medicalc integriert und automatisiert damit administrative Arbeit in öffentlichen Krankenhäusern. Sprachaufnahmen aus Untersuchungen, Operationen, Visiten und Rettungseinsätzen werden vom Olingo Medical LLM transkribiert und in strukturierte klinische Dokumentation und FHIR-Datensätze umgewandelt. Physische Dokumente werden per Olingo OCR digitalisiert. Das System läuft als air-gapped On-Premise-Installation auf dedizierten GPU-Servern und sichert damit volle Datensouveränität — Patientendaten verlassen das Krankenhausnetz nie.',
    highlights: [
      'Über 10 Deployments in öffentlichen Krankenhäusern',
      'Air-gapped — Daten verlassen das Krankenhaus nie',
      'Sprechertrennung für Rettungsdienst, Operation, Obduktion und Visite',
      'FHIR-strukturierte Ausgabe aus rohen Sprachaufnahmen',
      'OCR für gedruckte Arztberichte und externe Dokumente',
      'Direkte Integration mit Medicalc über MEX-Exchange-Server',
    ],
  },
  't-systems-connectivity': {
    tagline: 'Flotten-Konnektivitätsmanagement mit Predictive Analytics',
    clientCountry: 'Deutschland',
    industry: 'Automotive / Telekommunikation',
    description:
      'Ollsoft hat für T-Systems International eine Managed Connectivity Platform entwickelt, die das Management der Internet-Konnektivität von Fahrzeugflotten in einer einzigen Oberfläche zusammenführt. Die Plattform aggregiert mehrere Netzwerk-APIs und ermöglicht Administratoren, SIM-Lebenszyklen zu verwalten, den Datenverbrauch zu überwachen, Software-Updates nachzuverfolgen und den künftigen Bandbreitenbedarf mithilfe von ML-Modellen vorherzusagen, die auf historischen Flottendaten trainiert wurden.',
    highlights: [
      'Einheitliche API-Aggregation über mehrere Netzbetreiber hinweg',
      'Interaktive Flottenkarte mit Konnektivitätsstatus',
      'ML-gestützte Prognose des Datenvolumens',
      'SIM-Lebenszyklus-Management in Flottengröße',
      'Pre-Flight-Checks vor kritischen OTA-Software-Updates',
    ],
  },
  'derantwortpartner': {
    tagline: 'CRM + Dokumentenarchiv → sofortige KI-Antworten',
    clientCountry: 'Deutschland',
    industry: 'Beratung / Energie',
    description:
      'Ollsoft hat für Die Netzwerkpartner e.V., einen deutschen Beratungsverband, ein KI-gestütztes Wissensmanagement-System gebaut. Das System verbindet sich mit deren Microsoft Dynamics CRM, der SharePoint-Dokumentenbibliothek und dem Energiewirtschaftsgesetz (EnWG) und ermöglicht Beratern, in Echtzeit belegte, präzise Antworten auf Mitgliederanfragen zu erzeugen. Die Anwendung nutzt eine RAG-Architektur mit Vektordatenbank, gehostet auf EU-Infrastruktur für volle DSGVO-Konformität. Eine kontinuierliche Feedback-Schleife verbessert die Relevanz im Lauf der Zeit.',
    highlights: [
      'RAG über CRM-Datensätze, SharePoint und Gesetzestext (EnWG)',
      'Direkte Integration mit MS Dynamics 365',
      'In der EU gehostet — vollständig DSGVO-konform',
      'Chat-Modus für iteratives Nachfragen',
      'Feedback-Schleife für kontinuierliche Relevanzverbesserung',
    ],
  },
  'heggel-advisor': {
    tagline: 'KI-Fachberater für industrielle Beschichtungen',
    clientCountry: 'Deutschland',
    industry: 'Industrie / Fertigung',
    description:
      'Ollsoft hat für die Heggel GmbH einen rund um die Uhr verfügbaren KI-gestützten Fachberater und ein Tool zur Lead-Generierung gebaut. Das System führt Nutzer durch einen dreistufigen Prozess: ein Projektszenario beschreiben, einen dynamisch von der KI erzeugten Fragebogen beantworten und anschließend eine Produktempfehlung aus dem Heggel-Portfolio mit vollständiger technischer Begründung erhalten — fundiert durch historische Projektdaten und technische Datenblätter via RAG. In drei Wochen geliefert, um einen Messetermin zu treffen.',
    highlights: [
      'Dynamischer KI-Fragebogen je Szenario',
      'RAG über technische Datenblätter und Projekthistorie',
      'Vollständige Produktbegründung für Kunden und Fachpersonal',
      'In 3 Wochen zum Messestart geliefert',
      'In einen Managed Service mit transparenter Azure-Weiterberechnung überführt',
    ],
  },
  'rowanai': {
    tagline: 'KI-Agenten im Hintergrund steuern den Kanzleibetrieb — air-gapped, on-premise',
    clientCountry: 'Tschechien',
    industry: 'Recht',
    description:
      'Ollsoft hat für Rowan Legal eine air-gapped On-Premise-Infrastruktur für KI-Agenten gebaut. Mehrere autonome Agenten laufen kontinuierlich im Hintergrund und übernehmen die operative Ebene der Kanzlei: Klassifizierung der Mandatsannahme, internes Wissensabrufen, Orchestrierung der Recherche-Pipeline, Dokumenten-Routing und Compliance-Prüfungen. Nichts wird an eine externe API gesendet — die gesamte Inferenz läuft lokal auf dedizierter GPU-Hardware im Netz der Kanzlei. Mandatsdaten, Akten und interne Korrespondenz bleiben vollständig im Haus.',
    highlights: [
      'Vollständig air-gapped — keine externen API-Aufrufe, gesamte Inferenz on-premise',
      'Autonome Agenten übernehmen Mandatsannahme, Routing und Klassifizierung',
      'Internes Wissensabrufen über Akten und kanzleieigene Präzedenzfälle',
      'Recherche-Pipeline-Agenten finden relevante Rechtsprechung automatisch',
      'Compliance-Prüfungen laufen als Hintergrundprozesse statt manueller Reviews',
      'Mandatsdaten verlassen das Kanzleinetz nie',
    ],
  },
  'anywhere-legal': {
    tagline: 'KI-gestützte Verwaltung juristischer Dokumente',
    clientCountry: 'Tschechien',
    industry: 'Recht',
    description:
      'Ollsoft hat die Plattform Anywhere Legal um KI erweitert, um juristisches Fallmanagement und internationale Zusammenarbeit zu steuern. Die Lösung bietet einen zentralen KI-Workspace, der Scoping-, Pricing- und Recherche-Tools vereint. Kernkomponente ist ein KI-gestützter Dokumenteneditor, mit dem Anwälte Verträge mithilfe generativer KI entwerfen und ändern können. Olingo-Speech-Modelle übernehmen die juristische Transkription. Die gesamte Verarbeitung läuft auf privater EU-Cloud-Infrastruktur und sichert Datensouveränität und Mandantengeheimnis.',
    highlights: [
      'KI-Dokumenteneditor zum Entwerfen und Ändern von Verträgen',
      'Zentrale KI-Orchestrierung über Scoping, Pricing und Recherche',
      'Juristische Transkription über Olingo-Speech-Modelle',
      'Automatisierte Partnerauswahl auf Basis der Fallanforderungen',
      'Private EU-Cloud — vollständiges Mandantengeheimnis',
    ],
  },
  'eurowag-legislation': {
    tagline: 'Legal-Intelligence-Plattform für mehrere Rechtsräume',
    clientCountry: 'Tschechien / EU',
    industry: 'Kraftstoff & Telematik',
    description:
      'Ollsoft hat für Eurowag eine automatisierte Plattform zur Gesetzgebungsüberwachung gebaut, die regulatorische Änderungen in der EU sowie in tschechischen und slowakischen Rechtsräumen verfolgt, die den Kraftstoff- und Telematiksektor betreffen. LLMs analysieren amtliche Gesetzesblätter, juristische Newsletter und Lobbying-Aktivitäten und filtern nach dem spezifischen Geschäftsprofil von Eurowag. Ein täglicher Ampelbericht (rot/orange/grün) hilft Rechtsteams, sich auf kritische Compliance-Maßnahmen zu konzentrieren. Präzises Deep-Linking springt direkt zum relevanten Absatz im Quelldokument.',
    highlights: [
      'Überwacht die Gesetzgebungsregister der EU, Tschechiens und der Slowakei',
      'Tägliche Ampel-E-Mail-Briefings (rot/orange/grün)',
      'Präzise Deep-Links zu exakten Absätzen in Quell-PDFs',
      'KI-Feedback-Schleife reduziert Fehlalarme im Lauf der Zeit',
      'Zero-IT-Deployment — keine interne IT-Integration nötig',
    ],
  },
  'byteship': {
    tagline: 'KI-gestützte IDE für Log-Analyse',
    client: 'Intern / B2B-SaaS',
    industry: 'Entwickler-Tools',
    description:
      'ByteShip ist eine KI-gestützte IDE für die Analyse von Logdateien und das Debugging. Das System nutzt KI-Modelle, um komplexe Log-Ausgaben zu parsen, zu interpretieren und daraus umsetzbare Erkenntnisse herauszuarbeiten — und reduziert so die Zeit drastisch, die Entwickler für manuelles Debugging aufwenden.',
    highlights: [
      'KI-gestütztes Log-Parsing und Mustererkennung',
      'Reduziert die manuelle Debugging-Zeit drastisch',
      'Strukturierte Erkenntnisse aus rohen Log-Ausgaben',
    ],
  },
};

/** Returns the project's locale-resolved content fields (DE on /de, else EN). */
export function localizeProject(
  p: Project,
  locale: string,
): Pick<Project, 'tagline' | 'description' | 'client' | 'clientCountry' | 'industry' | 'highlights'> {
  const de = locale === 'de' ? PROJECT_DE[p.slug] : undefined;
  return {
    tagline: de?.tagline ?? p.tagline,
    description: de?.description ?? p.description,
    client: de?.client ?? p.client,
    clientCountry: de?.clientCountry ?? p.clientCountry,
    industry: de?.industry ?? p.industry,
    highlights: de?.highlights ?? p.highlights,
  };
}

export function getProjectBySlug(slug: string): Project | undefined {
  return PROJECTS.find((p) => p.slug === slug);
}

export const FEATURED_PROJECTS = PROJECTS.filter((p) => p.featured);
